import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from './Modal.jsx';
import { haptic } from '../../hooks.js';
import { Icon } from './Icon.jsx';
import { t } from '../../i18n/index.js';

/*
  Obvestila in potrditve — nadomestilo za alert(), confirm() in prompt().

  Zakaj sploh: brskalnikovi pogovorni okni ustavijo cel zavihek, jih ni
  mogoče oblikovati, na iOS-u v PWA izgledajo kot napaka in — najhuje —
  prompt('Za potrditev vpiši IZBRIŠI') je bil edini varovalni mehanizem pred
  trajnim brisanjem tečajnika z vsemi podatki.

  Uporaba:
    const { toast, confirm } = useUI();
    toast.ok('Shranjeno');
    if (await confirm({ title: 'Izbrišem?', danger: true })) ...
*/

const UIContext = createContext(null);

export const useUI = () => {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI zunaj <UIProvider>');
  return ctx;
};

let nextId = 1;

export function UIProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [ask, setAsk] = useState(null);
  const resolveRef = useRef(null);

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (kind, text, opts = {}) => {
      const id = nextId++;
      setToasts((t) => [...t, { id, kind, text, action: opts.action }]);
      // Kratek drget: potrditev en sunek, napaka dva. Na iOS-u v brskalniku
      // tega ni — zato je to samo dodatek k vidnemu obvestilu.
      haptic(kind === 'bad' ? [18, 60, 18] : 12);
      // Napake ostanejo dlje — človek jih mora uspeti prebrati.
      const ms = opts.duration ?? (kind === 'bad' ? 8000 : 4000);
      if (ms > 0) setTimeout(() => dismiss(id), ms);
      return id;
    },
    [dismiss],
  );

  const toast = useMemo(
    () => ({
      ok: (text, opts) => push('ok', text, opts),
      bad: (text, opts) => push('bad', text, opts),
      info: (text, opts) => push('info', text, opts),
      dismiss,
    }),
    [push, dismiss],
  );

  /** Vrne Promise<boolean>. Pri `typeToConfirm` mora uporabnik vpisati besedo. */
  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setAsk({ ...options });
    });
  }, []);

  const settle = useCallback((value) => {
    setAsk(null);
    resolveRef.current?.(value);
    resolveRef.current = null;
  }, []);

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

  return (
    <UIContext.Provider value={value}>
      {children}
      <Toasts items={toasts} onDismiss={dismiss} />
      {ask && <ConfirmDialog {...ask} onSettle={settle} />}
    </UIContext.Provider>
  );
}

/* ── Obvestila ─────────────────────────────────────────────── */

function Toasts({ items, onDismiss }) {
  if (typeof document === 'undefined') return null;
  // Spremenljivka zanke spodaj se imenuje `t` in bi zakrila prevajalno funkcijo.
  const closeLabel = t('Zapri obvestilo');
  return createPortal(
    <div className="toasts" role="region" aria-label={t('Obvestila')}>
      {items.map((t) => (
        <div
          key={t.id}
          className={`toast toast--${t.kind}`}
          /* Napaka mora bralnik zaslona prekiniti, potrditev ne. */
          role={t.kind === 'bad' ? 'alert' : 'status'}
          aria-live={t.kind === 'bad' ? 'assertive' : 'polite'}
        >
          <Icon
            name={t.kind === 'ok' ? 'check' : t.kind === 'bad' ? 'alert' : 'info'}
            size={18}
            className={t.kind === 'ok' ? 't-ok' : t.kind === 'bad' ? 't-bad' : 't-brand'}
          />
          <span className="toast__text">{t.text}</span>
          {t.action && (
            <button
              className="btn btn--sm btn--ghost"
              onClick={() => {
                t.action.onClick();
                onDismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button
            className="btn-icon btn-icon--sm"
            onClick={() => onDismiss(t.id)}
            aria-label={closeLabel}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}

/* ── Potrditev ─────────────────────────────────────────────── */

function ConfirmDialog({
  title,
  body,
  detail,
  confirmLabel = t('Potrdi'),
  cancelLabel = t('Prekliči'),
  danger = false,
  typeToConfirm = null,
  onSettle,
}) {
  const [typed, setTyped] = useState('');
  const blocked = typeToConfirm != null && typed.trim() !== typeToConfirm;

  return (
    <Modal
      size="sm"
      title={title}
      onClose={() => onSettle(false)}
      footer={
        <>
          <button
            className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`}
            disabled={blocked}
            onClick={() => onSettle(true)}
          >
            {confirmLabel}
          </button>
          <button className="btn btn--ghost" onClick={() => onSettle(false)}>
            {cancelLabel}
          </button>
        </>
      }
    >
      <div className="stack">
        {body && <p className="t-md">{body}</p>}

        {detail && (
          <ul className="stack stack--tight t-sm t-dim" style={{ paddingLeft: '1.1rem' }}>
            {detail.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        )}

        {typeToConfirm && (
          <label className="field">
            <span className="field__label">
              {t('Za potrditev vpiši')} <strong>{typeToConfirm}</strong>
            </span>
            <input
              className="input"
              value={typed}
              data-autofocus
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !blocked) onSettle(true);
              }}
              autoComplete="off"
              spellCheck="false"
            />
          </label>
        )}
      </div>
    </Modal>
  );
}
